// Error taxonomy for the AblyChannelRegistry. All errors extend
// AblyChannelRegistryError so consumers can `instanceof`-catch the family.
//
// See plan 060 TICKET-022 §"Errors" and the canonical INV list (INV-11/12/13/16).

import type { RegistryChannelOptions } from "@f3/shared";

export class AblyChannelRegistryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AblyChannelRegistryError";
  }
}

/**
 * INV-11: thrown synchronously by acquire methods (subscribeChannel,
 * subscribePresence, acquirePresencePusher) when a second acquire on the
 * same channel name uses ChannelOptions whose canonicalized hash differs
 * from the existing entry's. Registry-level invariant — Ably docs do not
 * define what happens when channels.get is called twice with different
 * options. We fail fast.
 */
export class RegistryOptionsConflictError extends AblyChannelRegistryError {
  readonly channelName: string;
  readonly existingOptions: RegistryChannelOptions | undefined;
  readonly requestedOptions: RegistryChannelOptions | undefined;

  constructor(
    channelName: string,
    existingOptions: RegistryChannelOptions | undefined,
    requestedOptions: RegistryChannelOptions | undefined,
  ) {
    super(
      `Channel "${channelName}" already acquired with options ${JSON.stringify(
        existingOptions,
      )}; requested ${JSON.stringify(
        requestedOptions,
      )}. All consumers on a channel must agree on options.`,
    );
    this.name = "RegistryOptionsConflictError";
    this.channelName = channelName;
    this.existingOptions = existingOptions;
    this.requestedOptions = requestedOptions;
  }
}

/**
 * INV-12: thrown by every method on a handle after release(). Calling
 * release() twice is a no-op (idempotent), but any other method call after
 * release throws this error.
 */
export class ChannelHandleReleasedError extends AblyChannelRegistryError {
  readonly channelName: string;
  readonly handleId: symbol;

  constructor(channelName: string, handleId: symbol) {
    super(
      `Handle ${handleId.toString()} for "${channelName}" was released; further operations are not permitted. Acquire a new handle.`,
    );
    this.name = "ChannelHandleReleasedError";
    this.channelName = channelName;
    this.handleId = handleId;
  }
}

/**
 * INV-16: thrown by acquirePresencePusher when another pusher handle is
 * already held on the same (descriptor, channelArgs) tuple. Single-pusher
 * invariant runtime guard — type system also prevents calling
 * acquirePresencePusher on non "pusher-and-reader" descriptors.
 */
export class PresencePusherAlreadyHeldError extends AblyChannelRegistryError {
  readonly channelName: string;
  readonly currentPusherHandleId: symbol;
  readonly requestingHandleId: symbol;

  constructor(
    channelName: string,
    currentPusherHandleId: symbol,
    requestingHandleId: symbol,
  ) {
    super(
      `Channel "${channelName}" already has pusher handle ${currentPusherHandleId.toString()}; handle ${requestingHandleId.toString()} cannot also push. Single-pusher invariant violated. Either release the current pusher first, or refactor so only one consumer pushes presence on this channel.`,
    );
    this.name = "PresencePusherAlreadyHeldError";
    this.channelName = channelName;
    this.currentPusherHandleId = currentPusherHandleId;
    this.requestingHandleId = requestingHandleId;
  }
}

/**
 * Defensive runtime guard. Should be unreachable given the type-system
 * constraints on acquirePresencePusher (only accepts descriptors with
 * presence === "pusher-and-reader"). If it does fire, the descriptor
 * was constructed via an escape hatch.
 */
export class PresenceNotSupportedOnChannelError extends AblyChannelRegistryError {
  readonly channelName: string;
  readonly descriptorPresence: string;

  constructor(channelName: string, descriptorPresence: string) {
    super(
      `Channel "${channelName}" descriptor declares presence: "${descriptorPresence}" — pusher acquires not allowed. Update the descriptor or acquire a reader instead.`,
    );
    this.name = "PresenceNotSupportedOnChannelError";
    this.channelName = channelName;
    this.descriptorPresence = descriptorPresence;
  }
}

/**
 * INV-13: thrown synchronously by acquire methods if the resolved channel
 * name violates Ably's documented rules (empty, starts with "[" or ":",
 * contains newlines).
 */
export class RegistryInvalidChannelNameError extends AblyChannelRegistryError {
  readonly channelName: string;
  readonly reason: string;

  constructor(channelName: string, reason: string) {
    super(
      `Channel name ${JSON.stringify(channelName)} is invalid: ${reason}. Per Ably's documentation, channel names must not be empty, must not start with "[" or ":", must not contain newline characters, and should be ≤2048 characters.`,
    );
    this.name = "RegistryInvalidChannelNameError";
    this.channelName = channelName;
    this.reason = reason;
  }
}
